import { z } from 'zod';

import { StudioRoleSchema } from './studio.js';

// Row shape returned by the studio-invites edge function (with token stripped)
// or by direct selects against studio_invites for the Members panel.
export const StudioInviteSchema = z.object({
  id: z.string().uuid(),
  studioId: z.string().uuid(),
  email: z.string().email(),
  role: StudioRoleSchema,
  personalMessage: z.string().nullable(),
  invitedByUserId: z.string().uuid().nullable(),
  expiresAt: z.string().datetime({ offset: true }),
  sentAt: z.string().datetime({ offset: true }),
  lastSentAt: z.string().datetime({ offset: true }),
  acceptedAt: z.string().datetime({ offset: true }).nullable(),
  acceptedByUserId: z.string().uuid().nullable(),
  revokedAt: z.string().datetime({ offset: true }).nullable(),
  revokedByUserId: z.string().uuid().nullable(),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
});
export type StudioInvite = z.infer<typeof StudioInviteSchema>;

// Result of the validate_studio_invite RPC. The portal narrows on `status`
// to render the right state at /invite/:token without minting a session.
export const InviteValidationStudioSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
});

export const InviteValidationResultSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('valid'),
    studio: InviteValidationStudioSchema,
    inviter: z.object({ displayName: z.string() }),
    expiresAt: z.string().datetime({ offset: true }),
    email: z.string().email(),
    personalMessage: z.string().nullable(),
  }),
  z.object({ status: z.literal('expired') }),
  z.object({ status: z.literal('revoked') }),
  z.object({ status: z.literal('accepted') }),
  z.object({ status: z.literal('not_found') }),
]);
export type InviteValidationResult = z.infer<typeof InviteValidationResultSchema>;

// Form schema for the "Invite member" modal in the Members panel.
export const SendInviteFormSchema = z.object({
  email: z.string().trim().min(1, 'Email is required').email('Enter a valid email address'),
  personalMessage: z.string().max(500, 'Personal message must be 500 characters or fewer').optional(),
});
export type SendInviteForm = z.infer<typeof SendInviteFormSchema>;

// Result of accept_studio_invite for an existing user.
export const AcceptInviteResultSchema = z.object({
  studio: InviteValidationStudioSchema,
  alreadyMember: z.boolean(),
});
export type AcceptInviteResult = z.infer<typeof AcceptInviteResultSchema>;

// Result of accept_studio_invite_as_new_user.
export const AcceptInviteAsNewUserResultSchema = z.object({
  invitingStudio: InviteValidationStudioSchema,
  personalStudio: InviteValidationStudioSchema,
});
export type AcceptInviteAsNewUserResult = z.infer<typeof AcceptInviteAsNewUserResultSchema>;

// Studio member row enriched with auth.users fields, returned by the
// list_studio_members RPC.
export const StudioMemberSummarySchema = z.object({
  memberId: z.string().uuid(),
  userId: z.string().uuid(),
  role: StudioRoleSchema,
  email: z.string().email().nullable(),
  displayName: z.string(),
  invitedAt: z.string().datetime({ offset: true }).nullable(),
  joinedAt: z.string().datetime({ offset: true }).nullable(),
});
export type StudioMemberSummary = z.infer<typeof StudioMemberSummarySchema>;
